/**
 * Phase 4.1 — Active WebRTC call bar.
 *
 * Floats at bottom-right of the viewport whenever the singleton WebRTC
 * client has an active call. Shows:
 *   - Customer phone
 *   - Live call state badge (connecting / ringing / in-call / ended)
 *   - Duration timer (mm:ss)
 *   - Hang Up button
 *   - Hidden <audio> sink for the remote stream
 *
 * Lifecycle contract (Phase 4.1.3)
 * --------------------------------
 * The bar remains visible for the ENTIRE active call — NOT just until
 * the SDK fires `destroy`/`purge`. When the backend issues the explicit
 * Call-Control bridge action, the `@telnyx/webrtc` SDK reparents the
 * browser's SIP leg and fires `destroy` on the local call object even
 * though the media path is still live server-side. Tying UI teardown
 * to that SDK event caused the bar to vanish mid-call (losing the Hang
 * Up button).
 *
 * Instead, the backend's `companies/{cid}/webrtc_active/{uid}` Firestore
 * doc is the authoritative lifecycle signal — it's written on initiate
 * and deleted by the webhook-driven `on_call_hangup`. This component
 * listens to that doc via `onSnapshot` and calls
 * `webrtcClient.markCallEndedByBackend()` only when the doc truly goes
 * away. Result: Hang Up stays reachable from the moment the call starts
 * until Telnyx confirms the call has ended (from either side).
 *
 * Mobile composer guard
 * ---------------------
 * Pinned `bottom-24 sm:bottom-4` so it never covers the Inbox composer
 * on mobile. Hidden remote-audio sink stays mounted so audio doesn't
 * drop on state churn.
 *
 * Dev-flag gating
 * ---------------
 * Renders nothing if `REACT_APP_WEBRTC_DEV !== 'true'`.
 */
import React, { useEffect, useRef, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { PhoneOff, Globe } from 'lucide-react';
import { db } from '../firebase';
import { getWebRTC, WEBRTC_DEV_ENABLED } from '../lib/webrtcClient';

const STATE_LABEL = {
  trying:     'Connecting…',
  requesting: 'Connecting…',
  ringing:    'Ringing customer…',
  early:      'Ringing customer…',
  active:     'Connected',
  held:       'On hold',
  hangup:     'Ending…',
  destroy:    'Connected',
  purge:      'Connected',
};

const STATE_DOT = {
  trying:     'bg-amber-400',
  requesting: 'bg-amber-400',
  ringing:    'bg-blue-400',
  early:      'bg-blue-400',
  active:     'bg-emerald-400',
  held:       'bg-amber-400',
  hangup:     'bg-rose-400',
  destroy:    'bg-emerald-400',
  purge:      'bg-emerald-400',
};

const formatDuration = (s) => {
  if (!s || s < 0) return '0:00';
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${m}:${String(sec).padStart(2, '0')}`;
};

export default function ActiveCallBar() {
  const [state, setState] = useState(getWebRTC().getState());
  const audioRef = useRef(null);
  // Read companyId + uid from the WebRTC singleton — it latches them on
  // first `/api/webrtc/token` response. This keeps the ActiveCallBar
  // independent of AuthContext, which isn't mounted at the root of the
  // app (the bar lives OUTSIDE <ProtectedRoute>).
  const companyId = state.companyId;
  const uid = state.uid;

  // Subscribe to the WebRTC singleton for activeCall state.
  useEffect(() => {
    if (!WEBRTC_DEV_ENABLED) return undefined;
    const unsub = getWebRTC().subscribe((s) => setState(s));
    return unsub;
  }, []);

  // Wire remote stream when it becomes available.
  useEffect(() => {
    const stream = state.activeCall?.remoteStream;
    if (audioRef.current && stream && audioRef.current.srcObject !== stream) {
      try { audioRef.current.srcObject = stream; } catch (_) { /* swallow */ }
    }
  }, [state.activeCall?.remoteStream]);

  // Backend-authoritative lifecycle: when `webrtc_active/{uid}` is
  // deleted, the call truly ended (either side hung up). Only then do
  // we tear down the bar. This is what keeps Hang Up reachable even
  // after the SDK's post-bridge destroy/purge.
  useEffect(() => {
    if (!WEBRTC_DEV_ENABLED) return undefined;
    if (!companyId || !uid) return undefined;
    const ref = doc(db, 'companies', companyId, 'webrtc_active', uid);
    // Track whether the doc has ever existed in this subscription — we
    // must NOT fire markCallEndedByBackend for the initial "does not
    // exist" snapshot that arrives before the user places a call.
    let seenExists = false;
    const unsub = onSnapshot(
      ref,
      (snap) => {
        if (snap.exists()) {
          seenExists = true;
          const data = snap.data() || {};
          if (data.endedAt && getWebRTC().getState().activeCall) {
            getWebRTC().markCallEndedByBackend();
          }
        } else if (seenExists && getWebRTC().getState().activeCall) {
          // Doc was deleted AFTER a call was in progress → backend
          // cleanup ran → tear down UI.
          getWebRTC().markCallEndedByBackend();
        }
      },
      (err) => {
        // Fail-open: if the listener errors out, don't lock the UI in
        // an un-closable state — but log loudly so we notice.
        // eslint-disable-next-line no-console
        console.warn('[ActiveCallBar] webrtc_active snapshot error', err);
      },
    );
    return () => unsub();
  }, [companyId, uid]);

  if (!WEBRTC_DEV_ENABLED) return null;
  const ac = state.activeCall;
  if (!ac) return null;

  // Post-bridge / post-hangup: the SDK reports `destroy`/`purge` but the
  // call itself may still be alive. Label it "Bridged" until the
  // backend listener confirms the call has fully ended (endedAt set).
  const sdkState = ac.state || 'idle';
  const effectiveState = ac.endedAt
    ? 'hangup'
    : (ac.sdkDetached && sdkState !== 'active' ? 'destroy' : sdkState);

  const label = STATE_LABEL[effectiveState] || effectiveState;
  const dot = STATE_DOT[effectiveState] || 'bg-slate-400';
  const isEnded = Boolean(ac.endedAt);
  const dur = ac.durationSecs || 0;
  // Hang Up is disabled ONLY once the call is definitively ended.
  // sdkDetached (post-bridge) must still allow Hang Up — that's the
  // whole point of this iteration.
  const hangupDisabled = isEnded;

  const onHangup = () => {
    try { getWebRTC().hangup(); } catch (_) { /* swallow */ }
  };

  return (
    <div
      className="fixed right-4 bottom-24 sm:bottom-4 z-[60] pointer-events-none"
      data-testid="active-call-bar"
    >
      <div
        className={`pointer-events-auto inline-flex items-center gap-3 rounded-full
                    border bg-white shadow-lg pl-3 pr-1 py-1
                    transition-all duration-200
                    ${isEnded ? 'border-rose-200 opacity-90' : 'border-slate-200'}`}
        data-testid="active-call-bar-pill"
        data-call-state={effectiveState}
      >
        <div className="flex items-center gap-1.5 text-emerald-700">
          <Globe className="w-3.5 h-3.5" />
          <span className="text-[11px] font-semibold uppercase tracking-wide">On this computer</span>
        </div>
        <span className="h-4 w-px bg-slate-200" />
        <div className="flex items-center gap-1.5">
          <span className={`inline-block w-2 h-2 rounded-full ${dot} ${effectiveState === 'active' ? 'animate-pulse' : ''}`} />
          <span className="text-xs font-medium text-slate-800">{label}</span>
        </div>
        <span className="text-xs text-slate-700 font-mono tabular-nums" data-testid="active-call-duration">
          {formatDuration(dur)}
        </span>
        <span className="hidden sm:inline text-xs text-slate-500 max-w-[160px] truncate" data-testid="active-call-destination">
          {ac.destination}
        </span>
        <button
          type="button"
          onClick={onHangup}
          disabled={hangupDisabled}
          className="inline-flex items-center gap-1 rounded-full bg-rose-600 hover:bg-rose-700 disabled:bg-rose-300 text-white text-xs font-medium px-3 py-1.5 transition-colors"
          data-testid="active-call-hangup-btn"
          aria-label="Hang up"
        >
          <PhoneOff className="w-3.5 h-3.5" />
          <span className="hidden sm:inline">Hang up</span>
        </button>
      </div>

      {/* Hidden remote-audio sink — must remain mounted so audio doesn't drop on state churn. */}
      <audio ref={audioRef} autoPlay playsInline data-testid="active-call-audio" />
    </div>
  );
}
